import React, { useState, useEffect } from 'react';

/**
 * Modale de cascade propriétaire ↔ biens.
 * - mode "archive" : archiver un propriétaire ayant des biens actifs liés
 *   → cases à cocher individuelles + « Tout archiver » / « Conserver tels quels ».
 * - mode "delete-blocked" : suppression bloquée (409) → liste cliquable des biens à gérer d'abord.
 */
export const ContactCascadeModal = ({
  open,
  mode,            // 'archive' | 'delete-blocked'
  contact,
  linkedProperties = [],
  hasActiveSearches = false,
  isAcquereur = false,
  isProprietaire = false,
  onClose,
  onConfirmArchive,  // (selectedPropertyIds) => void
}) => {
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    // Par défaut : tout coché (intention « archiver l'ensemble »)
    if (open && mode === 'archive') {
      setSelectedIds(linkedProperties.map((p) => p.id));
    }
  }, [open, mode, linkedProperties]);

  if (!open || !contact) return null;

  const fullName = `${contact.prenom || ''} ${contact.nom || ''}`.trim();
  const isMixteAlert = isAcquereur && isProprietaire && hasActiveSearches;

  const toggle = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const statusLabel = (s) => {
    const map = { active: 'En ligne', draft: 'Brouillon', sold: 'Vendu', archived: 'Archivé' };
    return map[s] || s || '—';
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
      data-testid="contact-cascade-overlay"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="contact-cascade-modal"
      >
        {/* Header */}
        <div className={`px-5 py-4 flex items-start gap-3 ${mode === 'delete-blocked' ? 'bg-red-50 border-b border-red-100' : 'bg-amber-50 border-b border-amber-100'}`}>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${mode === 'delete-blocked' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-600'}`}>
            <i className={`bi ${mode === 'delete-blocked' ? 'bi-exclamation-octagon-fill' : 'bi-archive-fill'} text-lg`}></i>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-slate-900 text-base">
              {mode === 'delete-blocked' ? 'Suppression impossible' : 'Archiver le propriétaire'}
            </h3>
            <p className="text-sm text-slate-600 mt-0.5">
              {mode === 'delete-blocked' ? (
                <>
                  <span className="font-semibold">{linkedProperties.length} bien(s) actif(s)</span> sont liés à <span className="font-semibold">{fullName}</span>. Gérez-les d'abord (archivez ou réaffectez) avant de supprimer ce contact.
                </>
              ) : (
                <>
                  <span className="font-semibold">{fullName}</span> est propriétaire de <span className="font-semibold">{linkedProperties.length} bien(s) actif(s)</span>. Voulez-vous aussi les archiver ?
                </>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="contact-cascade-close">
            <i className="bi bi-x-lg"></i>
          </button>
        </div>

        {/* Alerte rôle mixte */}
        {isMixteAlert && (
          <div className="mx-5 mt-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-800 flex items-start gap-2" data-testid="contact-cascade-mixte-alert">
            <i className="bi bi-info-circle-fill mt-0.5"></i>
            <span>Ce contact est aussi <strong>Acquéreur avec des recherches actives</strong>. L'archivage masquera également son profil de recherche.</span>
          </div>
        )}

        {/* Liste des biens */}
        <div className="flex-1 overflow-y-auto px-5 py-3 space-y-2">
          {linkedProperties.map((p) => (
            <div
              key={p.id}
              className="flex items-center gap-3 p-2.5 border border-slate-200 rounded-lg hover:bg-slate-50 transition"
              data-testid={`cascade-property-${p.id}`}
            >
              {mode === 'archive' && (
                <input
                  type="checkbox"
                  checked={selectedIds.includes(p.id)}
                  onChange={() => toggle(p.id)}
                  className="w-4 h-4 text-amber-600 rounded border-slate-300 focus:ring-amber-500 cursor-pointer flex-shrink-0"
                  data-testid={`cascade-property-check-${p.id}`}
                />
              )}
              <div className="flex-1 min-w-0">
                <a
                  href={`/properties/${p.id}/view`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-semibold text-blue-600 hover:underline truncate block"
                >
                  {p.titre}
                </a>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[11px] text-slate-500 num">Réf. {p.reference}</span>
                  <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded ${p.status === 'draft' ? 'bg-slate-100 text-slate-600' : 'bg-emerald-100 text-emerald-700'}`}>
                    {statusLabel(p.status)}
                  </span>
                </div>
              </div>
              <i className="bi bi-box-arrow-up-right text-slate-300 text-xs"></i>
            </div>
          ))}
        </div>

        {/* Footer actions */}
        <div className="px-5 py-4 border-t border-slate-200 flex flex-wrap items-center justify-end gap-2">
          {mode === 'delete-blocked' ? (
            <button
              onClick={onClose}
              className="px-4 py-2 bg-slate-800 hover:bg-slate-900 text-white rounded-lg text-sm font-semibold transition"
              data-testid="contact-cascade-understood"
            >
              Compris
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg text-sm font-semibold transition"
                data-testid="contact-cascade-cancel"
              >
                Annuler
              </button>
              <button
                onClick={() => onConfirmArchive([])}
                className="px-4 py-2 bg-white border border-slate-300 hover:bg-slate-50 text-slate-700 rounded-lg text-sm font-semibold transition"
                data-testid="contact-cascade-keep"
              >
                Conserver les biens
              </button>
              <button
                onClick={() => onConfirmArchive(selectedIds)}
                className="px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-semibold transition flex items-center gap-1.5"
                data-testid="contact-cascade-archive-selected"
              >
                <i className="bi bi-archive"></i>
                {selectedIds.length === linkedProperties.length
                  ? 'Tout archiver'
                  : `Archiver le contact + ${selectedIds.length} bien(s)`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContactCascadeModal;
